import { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { GridOverlay, type GridSelectionModel } from "@mui/x-data-grid-pro";
import { DataGrid } from "@/components/DataGrid";
import type { RegistryChangeAlertEntity } from "@/features/registryChangeAlert/types";
import { makeRegistryChangeAlertColumns } from "@/features/registryChangeAlert/utils/makeColumns";
import { ROWS_PER_PAGE_OPTIONS } from "@/features/registryChangeAlert/utils/constants";

const NoRowsOverlay: React.FC = () => (
  <GridOverlay>
    <Box sx={{ py: 4, textAlign: "center" }}>
      <Typography variant="body2" color="text.secondary">
        変動のあった物件はありません。
      </Typography>
    </Box>
  </GridOverlay>
);

const GRID_COMPONENTS = { NoRowsOverlay };

interface IProps {
  alerts: RegistryChangeAlertEntity[];
  rowCount: number;
  loading: boolean;
  page: number;
  pageSize: number;
  selectionModel: GridSelectionModel;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSelectionModelChange: (model: GridSelectionModel) => void;
  onAcquire: (alert: RegistryChangeAlertEntity) => void;
  onDismiss: (alert: RegistryChangeAlertEntity) => void;
  /** 読み取り専用ユーザーは「非表示」を押せない。 */
  isReadOnly: boolean;
}

/** 変動アラート一覧（サーバサイドページング・ページを跨いだ複数選択）。 */
export const RegistryChangeAlertDataGrid: React.FC<IProps> = ({
  alerts,
  rowCount,
  loading,
  page,
  pageSize,
  selectionModel,
  onPageChange,
  onPageSizeChange,
  onSelectionModelChange,
  onAcquire,
  onDismiss,
  isReadOnly,
}) => {
  const columns = useMemo(
    () => makeRegistryChangeAlertColumns(onAcquire, onDismiss, isReadOnly),
    [onAcquire, onDismiss, isReadOnly]
  );

  return (
    <DataGrid
      rows={alerts}
      columns={columns}
      loading={loading}
      stickyHeader
      checkboxSelection
      keepNonExistentRowsSelected
      selectionModel={selectionModel}
      onSelectionModelChange={onSelectionModelChange}
      paginationMode="server"
      rowCount={rowCount}
      page={page}
      pageSize={pageSize}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      disableSelectionOnClick
      disableColumnFilter
      components={GRID_COMPONENTS}
      getRowHeight={() => "auto"}
      headerHeight={44}
      sx={{
        "& .MuiDataGrid-cell": { py: 1 },
        // 所在が長い場合は列幅を広げず折り返す（操作列を画面内に収めるため）。
        "& .MuiDataGrid-cellContent": {
          whiteSpace: "normal",
          wordBreak: "break-all",
        },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700 },
      }}
    />
  );
};
